import type { FC } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Input } from '@/components/ui/input';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useTranslation } from '@/hooks/useTranslation';
import type { AdaptcutToolsStatus } from '../hooks/useAdaptcutTools';
import { matchesReference } from '../hooks/useAdaptcutTools';
import type { AdaptcutTool } from '../type/WearTypes';
import { AdaptcutToolPicker } from './AdaptcutToolPicker';

export type DiameterMode = 'adaptcut' | 'manual';

interface WearDiameterFieldProps {
  adaptcutStatus: AdaptcutToolsStatus;
  adaptcutTools: AdaptcutTool[];
  onAdaptcutRetry: () => void;
  /** Traceability reference of the selected tool. */
  reference: string | null;
  mode: DiameterMode;
  onModeChange: (mode: DiameterMode) => void;
  adaptcutTool: AdaptcutTool | null;
  onAdaptcutToolChange: (tool: AdaptcutTool) => void;
  manualValue: string;
  onManualChange: (value: string) => void;
  disabled: boolean;
}

/**
 * Cutting diameter of the tool, used for the whole history: taken from an
 * Adaptcut tool (users with the Adaptcut module) or typed. No default value.
 */
export const WearDiameterField: FC<WearDiameterFieldProps> = ({
  adaptcutStatus,
  adaptcutTools,
  onAdaptcutRetry,
  reference,
  mode,
  onModeChange,
  adaptcutTool,
  onAdaptcutToolChange,
  manualValue,
  onManualChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const adaptcutOffered = adaptcutStatus !== 'forbidden';
  const fromAdaptcut =
    mode === 'adaptcut' && adaptcutStatus === 'ok' && adaptcutTool?.diameterCut != null;
  const shownValue = fromAdaptcut ? String(adaptcutTool!.diameterCut) : manualValue;

  let help: string;
  if (fromAdaptcut || manualValue !== '') help = t('mlWear.context.diameterHelpUsed');
  else if (!adaptcutOffered) help = t('mlWear.context.diameterHelpRequired');
  else if (adaptcutStatus === 'error') help = t('mlWear.context.diameterHelpAdaptcutDown');
  else if (mode === 'adaptcut' && adaptcutTool === null)
    help = t('mlWear.context.diameterHelpPick');
  else if (
    adaptcutStatus === 'ok' &&
    !adaptcutTools.some((tool) => matchesReference(tool, reference))
  )
    help = t('mlWear.context.diameterHelpNoMatch', { reference: reference ?? '—' });
  else help = t('mlWear.context.diameterHelpRequired');

  return (
    <div
      className="ml-2.5 flex flex-col gap-2 border-l-2 border-border pl-3"
      data-testid="wear-diameter"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium text-foreground">{t('mlWear.context.diameter')}</span>
        {adaptcutOffered && (
          <SegmentedControl
            size="sm"
            aria-label={t('mlWear.context.diameterSource')}
            value={mode}
            onChange={onModeChange}
            options={(['adaptcut', 'manual'] as const).map((option) => ({
              value: option,
              label: t(
                option === 'adaptcut' ? 'mlWear.context.modeAdaptcut' : 'mlWear.context.modeManual'
              ),
              disabled,
              'data-testid': `diameter-mode-${option}`,
            }))}
          />
        )}
      </div>

      {mode === 'adaptcut' && adaptcutStatus === 'ok' && (
        <AdaptcutToolPicker
          tools={adaptcutTools}
          reference={reference}
          value={adaptcutTool}
          onChange={onAdaptcutToolChange}
        />
      )}
      {mode === 'adaptcut' && adaptcutStatus === 'error' && (
        <Callout size="inline" tone="error" data-testid="adaptcut-error">
          {t('mlWear.context.adaptcutError')}{' '}
          <Button variant="link" size="inline" onClick={onAdaptcutRetry}>
            {t('mlWear.context.retry')}
          </Button>
        </Callout>
      )}
      {mode === 'adaptcut' &&
        adaptcutStatus === 'ok' &&
        adaptcutTool &&
        adaptcutTool.diameterCut == null && (
          <Callout size="inline" tone="warning" data-testid="adaptcut-no-diameter">
            {t('mlWear.context.adaptcutNoDiameter')}
          </Callout>
        )}

      <div className="grid grid-cols-2 items-end gap-2">
        <label className="flex min-w-0 flex-col gap-1">
          <span className="flex justify-between gap-1 text-xs text-muted-foreground">
            {t('mlWear.context.diameterField')}
            <span className="font-mono text-2xs">{t('mlWear.context.units.mm')}</span>
          </span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step={0.1}
            data-testid="wear-diameter-input"
            value={shownValue}
            placeholder={t('mlWear.context.required')}
            disabled={disabled || fromAdaptcut}
            onChange={(event) => onManualChange(event.target.value)}
            className="px-2 tabular-nums disabled:bg-muted"
          />
        </label>
        {fromAdaptcut && (
          <Badge tone="primary" size="sm" caps className="mb-2 w-fit">
            {t('mlWear.context.adaptcutTag')}
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="wear-diameter-help">
        {help}
      </p>
    </div>
  );
};
